// src/views/Unauthorized.jsx
import { Link, useNavigate } from "react-router-dom";
import { useStateContext } from "../../context/ContextProvider";
import { ADMIN_DASHBOARD_PATH } from "../../router/Admin/AdminRoutes";
import { INSTITUTION_ADMIN_DASHBOARD_PATH } from "../../router/Institution/InstitutionRoutes";

const Unauthorized = () => {
  const { user } = useStateContext();
  const navigate = useNavigate();
  const role = user?.role?.name;

  const getMessage = () => {
    switch (role) {
      case "Administrator":
        return {
          message: "Admins are not allowed to access this section.",
          redirectPath: ADMIN_DASHBOARD_PATH,
          redirectLabel: "Go to Admin Dashboard",
        };
      case "Institution Administrator":
        return {
          message: "Institution Admins are not allowed to access this section.",
          redirectPath: INSTITUTION_ADMIN_DASHBOARD_PATH,
          redirectLabel: "Go to Institution Dashboard",
        };
      default:
        return {
          message: "You do not have permission to view this page.",
          redirectPath: "/",
          redirectLabel: "Go Home",
        };
    }
  };

  const { message, redirectPath, redirectLabel } = getMessage();

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-rose-50 via-amber-50 to-sky-50 flex items-center justify-center px-4 py-10">
      <div
        className="w-full max-w-xl rounded-2xl border border-black/5 bg-white/60 backdrop-blur-xl shadow-2xl
                   ring-1 ring-black/5 p-8 md:p-10 text-center animate-[fadeIn_300ms_ease-out]"
      >
        {/* Icon */}
        <div className="mx-auto mb-6 h-14 w-14 grid place-items-center rounded-full bg-red-100 text-red-600 shadow">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-7 w-7"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M12 1a5 5 0 00-5 5v3H6a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2v-8a2 2 0 00-2-2h-1V6a5 5 0 00-5-5zm-3 8V6a3 3 0 116 0v3H9zm3 4a2 2 0 110 4 2 2 0 010-4z" />
          </svg>
        </div>

        {/* Heading */}
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-neutral-900">
          403 · Unauthorized
        </h1>

        {/* Subcopy */}
        <p className="mt-3 text-neutral-700 leading-relaxed">{message}</p>

        {role && (
          <p className="mt-1 text-sm text-neutral-500">
            Signed in as <span className="font-medium">{role}</span>.
          </p>
        )}

        {/* Actions */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to={redirectPath}
            className="inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold text-white
                       bg-primary-dark hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
                       focus-visible:ring-primary-dark/60 shadow transition active:scale-[0.99]"
            aria-label={redirectLabel}
          >
            {redirectLabel}
          </Link>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-medium
                       bg-white/80 text-neutral-800 border border-black/10
                       hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
                       focus-visible:ring-neutral-400 transition"
            aria-label="Go back"
          >
            Go Back
          </button>
        </div>

        {/* Secondary help */}
        <div className="mt-6 text-xs text-neutral-500">
          If you believe this is an error, please contact your system
          administrator.
        </div>
      </div>
    </div>
  );
};

export default Unauthorized;
